import React, { useState, memo } from 'react';
import { Typography, IconButton, TextField, TableCell, TableRow } from '@mui/material';
import { getIn } from 'formik';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import NumberFormatText from '../../common/form/NumberFormatText';
import { getNumberComma } from '../../../utils/numberUtils';

const CostCalcForm = ({ 
  formik, 
  index, 
  cost, 
  handleRemove, 
  handleOpen, 
  itemCdOptions 
}) => {
  const prefix = `costCalcs[${index}]`;
  const [toggleDesc, setToggleDesc] = useState(false);

  // 금액 길이에 따른 폰트 크기 조절
  const getAdaptiveFontSize = (value) => {
    const stringValue = String(value || '');
    if (stringValue.length > 20) return '0.65rem';
    if (stringValue.length > 16) return '0.8rem';
    return '0.875rem';
  };

  /**
   * 필드 렌더러 (일반/조정 행 공용 사용 가능)
   */
  const renderField = (fieldName, typeKey, isGray = false) => {
   
    const fullName = `${prefix}.${fieldName}`;
    const typePath = `${prefix}.${typeKey}`;
    const currentType = getIn(formik.values, typePath) || (typeKey === 'salesType' ? 'unitVal' : 'priceVal');
    const isSelected = currentType === fieldName;
    const isFocused = document.activeElement.name === fullName;
    const isAdjustmentRow = cost.masterTyp === 'BAL' || !!cost.isAdjustment;

    return (
      <TableCell 
        sx={{ 
          backgroundColor: isGray ? '#f8f8f8' : 'inherit', 
          p: '4px !important' 
        }}
      >
        <TextField
          size="small"
          variant="standard"
          fullWidth
          name={fullName}
          value={isFocused ? (getIn(formik.values, fullName) ?? '') : (cost[fieldName] ?? '')}
          onChange={(e) => {
            const val = e.target.value.replace(/[^0-9-.]/g, '');
            formik.setFieldValue(fullName, val);
            
          }}
          onFocus={() => {
            if (!isSelected && !isAdjustmentRow) {
              formik.setFieldValue(typePath, fieldName);
            }
          }}
          sx={{ 
            backgroundColor: (isSelected && !isAdjustmentRow) ? '#fff9c4 !important' : 'transparent',
            '& .MuiInputBase-input': { 
              textAlign: 'right', 
              fontSize: getAdaptiveFontSize(cost[fieldName]),
              padding: '4px 0'
            } 
          }}
          InputProps={{ inputComponent: NumberFormatText }}
        />
      </TableCell>
    );

  };

  // [핵심] 마스터 타입이 'BAL'인 경우 (금액 조정 행)
    if (cost.masterTyp === 'BAL' || cost.isAdjustment) {
    return (
      <TableRow sx={{ '& .MuiTableCell-root': { textAlign: 'center', backgroundColor: '#fdfdfd' } }}>
        <TableCell align="center">{index + 1}</TableCell>
                
        <TableCell sx={{ width: 250, textAlign: 'left !important' }}>
          {/* 1. 화면 표시용 (텍스트만) */}
          <Typography variant="body2" sx={{ lineHeight: '32px', fontWeight: 500 }}>
            {getIn(formik.values, `${prefix}.productNm`) || '금액 조정'}
          </Typography>

          {/* 2. 데이터 전송용 (Hidden Input) */}
          <input 
            type="hidden" 
            name={`${prefix}.productNm`} 
            value={getIn(formik.values, `${prefix}.productNm`) || '금액 조정'} 
          />
        </TableCell>
        
        <TableCell>{/* 상세 버튼 없음 */}</TableCell>
        
        {/* 수량 및 단위 - 직접 입력은 막되 값은 유지 (Hidden 처리와 동일 효과) */}
        <TableCell sx={{ p: '2px 4px !important' }}>
          <div style={{ textAlign: 'center', color: '#ccc', fontSize: '0.8rem' }}>-</div>
          <input type="hidden" name={`${prefix}.expCnt`} value={getIn(formik.values, `${prefix}.expCnt`) ?? 1} />
        </TableCell>
        <TableCell sx={{ p: '2px 4px !important' }}>
          <div style={{ textAlign: 'center', color: '#ccc', fontSize: '0.8rem' }}>-</div>
          <input type="hidden" name={`${prefix}.productUnit`} value={getIn(formik.values, `${prefix}.productUnit`) ?? ''} />
        </TableCell>

        <TableCell sx={{ p: '4px !important' }}>
          <div style={{ textAlign: 'right', color: '#ccc', pr: 1 }}>-</div>
          {/* 내부적으로 0을 유지하여 저장 시 전송되도록 함 */}
          <input 
            type="hidden" 
            name={`${prefix}.unitVal`} 
            value={0} 
          />
        </TableCell>
        
        {/* 매출 금액 - renderField가 내부적으로 name, value, onChange를 처리함 */}
        {renderField('saleVal', 'salesType', true)}
        {renderField('saleValIn', 'salesType', true)}

        {/* 매입 단가 - 가림 (화면은 '-', 데이터는 0) */}
        <TableCell sx={{ p: '4px !important' }}>
          <div style={{ textAlign: 'right', color: '#ccc', pr: 1 }}>-</div>
          {/* 내부적으로 0을 유지하여 저장 시 전송되도록 함 */}
          <input 
            type="hidden" 
            name={`${prefix}.priceVal`} 
            value={0} 
          />
        </TableCell>
        
        {/* 매입 금액 - renderField가 내부적으로 name, value, onChange를 처리함 */}
        {renderField('buyVal', 'purchaseType', true)}
        {renderField('priceValIn', 'purchaseType', true)}

        <TableCell>
          <Typography variant="body2" sx={{ textAlign: 'right', pr: 0.5 }}>
            {getNumberComma(cost.profit || 0)}
          </Typography>
        </TableCell>
        
        <TableCell sx={{ backgroundColor: '#f5f4f4', textAlign: 'center' }}>0.00 %</TableCell>
        
        <TableCell sx={{ width: 60 }}>
          {/* 아이템 코드: Formik 연동 */}
          <Typography sx={{ fontSize: '0.8125rem', color: '#555' }}>
            {getIn(formik.values, `${prefix}.itemCd`) || 'BAL'}
          </Typography>
        </TableCell>
        
        <TableCell align="left" sx={{ width: 150, p: '4px 8px !important' }}>
          {/* 매입처: Formik 연동 */}
          <span style={{ fontSize: '0.85rem' }}>
            {getIn(formik.values, `${prefix}.sellPartnerNm`) || '-'}
          </span>
        </TableCell>
        
        <TableCell align="center">
          <IconButton onClick={handleRemove}><DeleteIcon /></IconButton>
        </TableCell>
      </TableRow>
    );
  }


  // [핵심] 일반 행 렌더링
  return (
    <>
      <TableRow sx={{ '& .MuiTableCell-root': { textAlign: 'center' } }}>
        <TableCell align="center" rowSpan={toggleDesc ? 2 : 1}>{index + 1}</TableCell>
        <TableCell sx={{ width: 250, textAlign: 'left !important' }}>
          {cost.masterTyp === 'N' ? (
            <Typography variant="body2" sx={{ lineHeight: '32px' }}>{cost.productNm}</Typography>
          ) : (
            <TextField
              variant="standard" size="small" fullWidth 
              name={`${prefix}.productNm`}
              value={getIn(formik.values, `${prefix}.productNm`) ?? ''}
              onChange={(e) => formik.setFieldValue(`${prefix}.productNm`, e.target.value)}
            />
          )}
        </TableCell>
        <TableCell>
          <IconButton onClick={() => setToggleDesc(!toggleDesc)}><InfoIcon /></IconButton>
        </TableCell>

        {['expCnt', 'productUnit'].map((field) => (
          <TableCell key={field} sx={{ p: '2px 4px !important' }}>
            <TextField
              variant="standard" size="small" fullWidth
              name={`${prefix}.${field}`}
              value={getIn(formik.values, `${prefix}.${field}`) ?? ''}
              onChange={(e) => formik.setFieldValue(`${prefix}.${field}`, e.target.value)}
              inputProps={{ style: { textAlign: 'center', fontSize: '0.8rem', padding: '4px 0' } }}
            />
          </TableCell>
        ))}

        {renderField('unitVal', 'salesType')}
        {renderField('saleVal', 'salesType', true)}
        {renderField('saleValIn', 'salesType', true)}

        {renderField('priceVal', 'purchaseType')}
        {renderField('buyVal', 'purchaseType', true)}
        {renderField('priceValIn', 'purchaseType', true)}

        <TableCell>
          <Typography variant="body2" sx={{ textAlign: 'right', pr: 0.5 }}>
            {getNumberComma(cost.profit || 0)}
          </Typography>
        </TableCell>
        <TableCell sx={{ backgroundColor: '#f5f4f4', textAlign: 'center' }}>
          {`${Number(cost.profitRate || 0).toFixed(2)} %`}
        </TableCell>
        <TableCell sx={{ width: 60 }}>
          <Typography sx={{ fontSize: '0.8125rem', textAlign: 'center' }}>
            {cost?.itemCd || getIn(formik.values, `${prefix}.itemCd`) || '-'}
          </Typography>
        </TableCell>
        <TableCell align="left" sx={{ width: 150, p: '4px 8px !important' }}>
          <span style={{ fontSize: '0.85rem', marginRight: '4px' }}>{cost.sellPartnerNm || '-'}</span>
          <IconButton size="small" onClick={() => handleOpen(index)}>
            <SearchIcon sx={{ fontSize: '1.1rem' }} />
          </IconButton>
        </TableCell>
        <TableCell align="center">
          <IconButton onClick={handleRemove}><DeleteIcon /></IconButton>
        </TableCell>
      </TableRow>

      {toggleDesc && (
        <TableRow>
          <TableCell colSpan={12}>
            <TextField
              size="small" fullWidth multiline name={`${prefix}.productDesc`}
              value={getIn(formik.values, `${prefix}.productDesc`) ?? ''}
              onChange={(e) => formik.setFieldValue(`${prefix}.productDesc`, e.target.value)}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default memo(CostCalcForm, (prev, next) => {
  return (
    prev.cost === next.cost &&
    prev.index === next.index &&
    getIn(prev.formik.values, `costCalcs[${prev.index}].salesType`) === getIn(next.formik.values, `costCalcs[${next.index}].salesType`) &&
    getIn(prev.formik.values, `costCalcs[${prev.index}].purchaseType`) === getIn(next.formik.values, `costCalcs[${next.index}].purchaseType`)
  );
});
